import { createSlice } from '@reduxjs/toolkit';
import { SortDirection } from 'common/constant';
import moment from 'moment';

type ShipMovementSearchState = {
  showSearchModal: boolean;
  showSortModal: boolean;
  filterSearchData: any[];
  sortSelected: {
    name: string;
    value: string;
  } | null;
  sortDirection: string;
  filterSearchTotal: number;
  filterInitialSearchData: any[];
  loading: boolean;
};

const initialState: ShipMovementSearchState = {
  showSearchModal: true,
  showSortModal: false,
  loading: false,
  sortSelected: null,
  sortDirection: '',
  filterInitialSearchData: [],
  filterSearchData: [],
  filterSearchTotal: 0,
};

const handleSortData = ({ data, sortType, direction }: { data: any[]; sortType: string; direction: string }): any[] => {
  let newData: any[] = [];
  switch (sortType) {
    case 'INFO':
    case 'POL':
    case 'POD':
    case 'VESSEL':
    case 'VOYNO':
    case 'STATUS':
      newData = data.sort((a: any, b: any) => {
        const itemA = (a[sortType] || '')?.toLowerCase();
        const itemB = (b[sortType] || '')?.toLowerCase();

        if (itemA > itemB) return direction === SortDirection.DESC ? -1 : 1;
        if (itemA < itemB) return direction === SortDirection.DESC ? 1 : -1;
        return 0;
      });
      break;
    case 'ETDSORT':
    case 'ETASORT':
    case 'ATDSORT':
    case 'ATASORT':
      newData = data.sort((a: any, b: any) => {
        const itemA = a[sortType] ? Number(a[sortType].padEnd(12, '0')) : 0;
        const itemB = b[sortType] ? Number(b[sortType].padEnd(12, '0')) : 0;

        if (itemA > itemB) return direction === SortDirection.DESC ? -1 : 1;
        if (itemA < itemB) return direction === SortDirection.DESC ? 1 : -1;
        return 0;
      });
      break;
    case 'UPTIMESORT':
      newData = data.sort((a: any, b: any) => {
        const itemA = a[sortType] ? moment(a[sortType]).toDate().getTime() : 0;
        const itemB = b[sortType] ? moment(b[sortType]).toDate().getTime() : 0;

        if (itemA > itemB) return direction === SortDirection.DESC ? -1 : 1;
        if (itemA < itemB) return direction === SortDirection.DESC ? 1 : -1;
        return 0;
      });
      break;
    default:
      newData = data.sort((a: any, b: any) => {
        const itemA = a['ETDSORT'] ? Number(a['ETDSORT'].padEnd(12, '0')) : 0;
        const itemB = b['ETDSORT'] ? Number(b['ETDSORT'].padEnd(12, '0')) : 0;

        if (itemA > itemB) return direction === SortDirection.DESC ? -1 : 1;
        if (itemA < itemB) return direction === SortDirection.DESC ? 1 : -1;

        if (itemA === itemB) {
          const vesselItemA = (a['VESSEL'] || '')?.toLowerCase();
          const vesselItemB = (b['VESSEL'] || '')?.toLowerCase();

          if (vesselItemA > vesselItemB) return direction === SortDirection.DESC ? -1 : 1;
          if (vesselItemA < vesselItemB) return direction === SortDirection.DESC ? 1 : -1;

          if (vesselItemA === vesselItemB) {
            const voynoItemA = (a['VOYNO'] || '')?.toLowerCase();
            const voynoItemB = (b['VOYNO'] || '')?.toLowerCase();

            if (voynoItemA > voynoItemB) return direction === SortDirection.DESC ? -1 : 1;
            if (voynoItemA < voynoItemB) return direction === SortDirection.DESC ? 1 : -1;
          }
        }
        return 0;
      });
      break;
  }

  return newData;
};

export const shipMovementSearchSlice = createSlice({
  name: 'shipMovementSearch',
  initialState,
  reducers: {
    toggleSearchModal: (state) => {
      if (!state.showSearchModal) {
        state.showSortModal = false;
      }
      state.showSearchModal = !state.showSearchModal;
    },
    toggleSortModal: (state) => {
      if (!state.showSortModal) {
        state.showSearchModal = false;
      }
      state.showSortModal = !state.showSortModal;
    },
    setSortSelected: (state, action) => {
      state.sortSelected = action.payload;
      state.sortDirection = SortDirection.DESC;

      if (state.filterSearchData?.length) {
        if (action.payload) {
          state.filterSearchData = handleSortData({
            data: state.filterSearchData,
            sortType: state.sortSelected?.value!,
            direction: state.sortDirection,
          });
        } else {
          state.filterSearchData = handleSortData({
            data: state.filterSearchData,
            sortType: '',
            direction: state.sortDirection,
          });
        }
      }

      state.showSortModal = false;
      state.loading = false;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload.sortDirection;

      if (state.sortSelected) {
        state.filterSearchData = handleSortData({
          data: state.filterSearchData,
          sortType: state.sortSelected?.value!,
          direction: state.sortDirection,
        });
      }

      state.loading = false;
    },
    filterShipMovement: (state, action) => {
      state.loading = true;
      if (!state.sortDirection) {
        state.sortDirection = SortDirection.ASC;
      }
      state.sortSelected = state.sortSelected || { name: '', value: '' };
    },
    filterShipMovementSuccess: (state, action) => {
      const data = action.payload.records?.map((item: any) => {
        const isArrivalOnTime =
          item?.ETADATE && item?.ATADATE
            ? moment(item.ETADATE).startOf('day').diff(moment(item.ATADATE).startOf('day')) >= 0
            : true;
        const isDepartureOnTime =
          item?.ETDDATE && item?.ATDDATE
            ? moment(item.ETDDATE).startOf('day').diff(moment(item.ATDDATE).startOf('day')) >= 0
            : true;
        const displayDelayText = !!(
          (item?.ATA && item?.ETA && !isArrivalOnTime) ||
          (item?.ATD && item?.EDT && !isDepartureOnTime)
        );

        return { ...item, STATUS: displayDelayText ? '遅延' : '' };
      });

      state.filterSearchData = [...data];
      state.filterInitialSearchData = [...data];

      if (state.sortSelected && state.sortDirection) {
        state.filterSearchData = handleSortData({
          data: state.filterSearchData,
          direction: state.sortDirection,
          sortType: state.sortSelected.value,
        });
      }
      state.filterSearchTotal = action.payload.total;
      state.showSearchModal = false;
      state.showSortModal = false;
      state.loading = false;
    },
    filterShipMovementFailure: (state) => {
      state.loading = false;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    cleanData: (state) => {
      return {
        ...initialState,
      };
    },
    clearSearchData: (state) => {
      state.showSearchModal = true;
      state.showSortModal = false;
      state.sortSelected = null;
      state.sortDirection = '';
      state.filterSearchData = [];
      state.filterSearchTotal = 0;
    },
  },
});

export const {
  toggleSearchModal,
  toggleSortModal,
  setSortDirection,
  setSortSelected,
  filterShipMovementSuccess,
  filterShipMovement,
  filterShipMovementFailure,
  setLoading,
  cleanData,
  clearSearchData,
} = shipMovementSearchSlice.actions;

export default shipMovementSearchSlice.reducer;
